/**
 * Size from risk: the feature that beats the native ticket. The trader types where the stop
 * goes and how much of the account this trade may lose; the size follows. Integer arithmetic
 * on cTrader units × 100 — never a float lot count.
 */

export interface SizingInput {
  /** Corrected equity from the snapshot. */
  equity: number
  /** Percent of equity to risk, e.g. 0.5. */
  riskPercent: number
  /** Reference price: ask for a buy, bid for a sell. */
  price: number
  stopLoss: number
  /** cTrader units × 100. */
  minVolume: number
  maxVolume: number
  /** The step the broker accepts; the minimum is a safe default. */
  stepVolume?: number
}

export interface Sizing {
  /** cTrader units × 100, on the step, inside [min, max]. Zero when nothing can be sized. */
  volume: number
  /** What that volume actually risks, as percent of equity — differs from the request when
   * the step or a bound moved it. */
  riskPercent: number | null
  reason: 'ok' | 'no-stop' | 'no-equity' | 'below-minimum' | 'capped'
}

export function sizeFromRisk(input: SizingInput): Sizing {
  // 1.1 − 1.098 is 0.0020000000000000018 in IEEE 754, which would floor a clean half lot
  // down a step. Prices carry at most a handful of decimals; ten is plenty and exact enough.
  const distance = Number(Math.abs(input.price - input.stopLoss).toFixed(10))
  if (!(distance > 0) || !(input.price > 0)) return { volume: 0, riskPercent: null, reason: 'no-stop' }
  if (!(input.equity > 0) || !(input.riskPercent > 0)) return { volume: 0, riskPercent: null, reason: 'no-equity' }

  const step = Math.max(1, Math.floor(input.stepVolume ?? input.minVolume))
  const riskAmount = (input.equity * input.riskPercent) / 100
  const units = riskAmount / distance
  const raw = Math.floor((units * 100) / step) * step

  if (raw < input.minVolume) {
    return { volume: 0, riskPercent: riskOf(input.minVolume, distance, input.equity), reason: 'below-minimum' }
  }
  if (raw > input.maxVolume) {
    return { volume: input.maxVolume, riskPercent: riskOf(input.maxVolume, distance, input.equity), reason: 'capped' }
  }
  return { volume: raw, riskPercent: riskOf(raw, distance, input.equity), reason: 'ok' }
}

function riskOf(volume: number, distance: number, equity: number): number {
  return ((volume / 100) * distance) / equity * 100
}

/** A stop given as a distance in pips, turned into a price. */
export function stopFromPips(price: number, pips: number, side: 'Buy' | 'Sell', digits: number): number {
  const pip = Math.pow(10, -(digits - 1))
  const raw = side === 'Buy' ? price - pips * pip : price + pips * pip
  return Number(raw.toFixed(digits))
}

/** Lots for display only. The number that travels is always the integer volume. */
export function lotsOf(volume: number, lotSize: number): number {
  return lotSize > 0 ? volume / 100 / lotSize : 0
}
